import { ActionIcon, Box, Group, Input, InputBase, Text } from "@mantine/core";
import { useElementSize } from "@mantine/hooks";
import { isString } from "lodash";

import { openedInputStyle } from "../../utils";
import { DropdownChevron } from "../Chevron";

import { IconX } from "@/components/icons";
import { getSmallerSize } from "@/utils/getSmallerSize";

import type { MantineSize, MantineStyleProps } from "@mantine/core";
import type { FC, PropsWithChildren } from "react";
import type { DropdownPosition } from "../../types";
import type { DropdownChevronProps } from "../Chevron";

export type DropdownBaseTargetProps = PropsWithChildren<{
  clearable?: boolean;
  disabled?: boolean;
  iconVariant?: DropdownChevronProps["variant"];
  onBlur?: () => void;
  onClear?: () => void;
  onClick?: () => void;
  onFocus?: () => void;
  opened?: boolean;
  placeholder?: string;
  position?: DropdownPosition;
  size?: MantineSize;
  value?: string | null;
}> &
  MantineStyleProps;

export const DropdownBaseTarget: FC<DropdownBaseTargetProps> = ({
  clearable = false,
  children,
  disabled = false,
  iconVariant,
  onBlur,
  onClear,
  onClick,
  onFocus,
  opened = false,
  placeholder,
  position,
  size,
  value,
  ...props
}) => {
  const { ref, width } = useElementSize();
  const currentSize = getSmallerSize(size);
  const isClearable = clearable && !!value;
  const inputStyle = position ? openedInputStyle[position] : undefined;

  return (
    <InputBase
      component="button"
      disabled={disabled}
      onBlur={onBlur}
      onClick={onClick}
      onFocus={onFocus}
      pointer
      rightSection={
        <Group ref={ref} px="md" gap="sm" wrap="nowrap">
          {isClearable && (
            <ActionIcon
              color="red"
              size={currentSize}
              variant={iconVariant}
              onClick={onClear}
              disabled={disabled}
            >
              <IconX size={24} />
            </ActionIcon>
          )}
          <DropdownChevron
            disabled={disabled}
            onClick={onClick}
            opened={opened}
            size={currentSize}
            variant={iconVariant}
          />
        </Group>
      }
      rightSectionPointerEvents={isClearable ? undefined : "none"}
      rightSectionWidth="auto"
      size={currentSize}
      styles={{
        input: opened ? inputStyle : undefined,
      }}
      type="button"
      {...props}
    >
      <Box mr={width}>
        {children ? (
          isString(children) ? (
            <Text size={currentSize} truncate>
              {children}
            </Text>
          ) : (
            children
          )
        ) : (
          <Input.Placeholder>{placeholder}</Input.Placeholder>
        )}
      </Box>
    </InputBase>
  );
};
